
import React from "react";
import { MovieContext } from "./MovieContext";
import { useMovieOperations } from "@/hooks/useMovieOperations";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const MovieProviderContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading: authLoading } = useAuth();
  const movieOperations = useMovieOperations();

  // Show loading spinner while auth is loading
  if (authLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 text-mono-seasalt animate-spin" />
        <p className="mt-4 text-mono-seasalt/70">Authenticating...</p>
      </div>
    );
  }

  // Show loading spinner while initial movies are loading (but not for subsequent local updates)
  if (movieOperations.isLoading && movieOperations.allMovies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 text-mono-seasalt animate-spin" />
        <p className="mt-4 text-mono-seasalt/70">Loading movie collection...</p>
      </div>
    );
  }

  // Create stable context value with all required properties
  const contextValue = {
    ...movieOperations,
    // Ensure these properties are available for the undo system
    handleMovieRemoval: movieOperations.handleMovieRemoval,
    isPending: movieOperations.isPending,
    undoPendingAction: movieOperations.undoAction,
    // Expose allMovies for search result state checking
    allMovies: movieOperations.allMovies,
    // Expose refetchMovies for manual refresh only
    refetchMovies: movieOperations.refetchMovies
  };

  return (
    <MovieContext.Provider value={contextValue}>
      {children}
    </MovieContext.Provider>
  );
};
